'use client'

import { useState, useMemo } from 'react'
import {
  Loader2,
  X,
  Coins,
  Globe,
  Blocks,
  CircleDollarSign,
  InfinityIcon,
  FileText,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { popModal, pushModal } from '@/components/modals'
import type { ProjectSubscriptionPlan } from '@/lib/types'
import { CancelModalBackgroundSimple } from './cancel-modal-background-simple'

interface SubscribeCancelModalProps {
  subscriptionId: string
  deployUrl: string
  projectId: string
  onSuccess?: () => void
  plan?: ProjectSubscriptionPlan
  nextRenewalAt?: number
  isWorkspaceSubscription?: boolean
}

// 功能卡片组件
function FeatureCard({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="relative flex flex-col justify-center items-center p-3 gap-2 bg-white/10 rounded-xl h-[116px] flex-1 min-w-0">
      {/* 图标 - 居中 */}
      <div className="w-10 h-10 flex-shrink-0">{icon}</div>

      {/* 红色叉号 - 绝对定位在图标高度2/3的位置，靠右 */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-label="Feature removed"
        className="absolute right-[42px] top-[32px]"
      >
        <title>Feature removed</title>
        <path
          d="M3 3L13 13M13 3L3 13"
          stroke="#FF5537"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>

      {/* 文字 */}
      <div className="text-xs text-white text-center leading-tight px-1">
        {text}
      </div>
    </div>
  )
}

export function SubscribeCancelModal({
  subscriptionId,
  deployUrl,
  projectId,
  onSuccess,
  plan = 'basic',
  nextRenewalAt,
  isWorkspaceSubscription = false,
}: SubscribeCancelModalProps) {
  const t = useTranslations('pricing_v2.subscribe_cancel_modal')
  const [isLoading, setIsLoading] = useState(false)

  const planName = useMemo(() => {
    return plan.charAt(0).toUpperCase() + plan.slice(1)
  }, [plan])

  // 计算剩余天数
  const daysLeft = useMemo(() => {
    if (!nextRenewalAt) return 0
    const now = Date.now()
    const diff = nextRenewalAt - now
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }, [nextRenewalAt])

  // 根据套餐类型获取对应的功能文案
  const getFeatureCredits = () => {
    if (plan === 'premium') {
      return t('feature_credits_premium') // "500 credits /month"
    }
    return t('feature_credits') // "200 credits /month"
  }

  const onCancel = () => {
    // 关闭当前 modal，打开二次确认 modal
    popModal()
    setTimeout(() => {
      pushModal('CancelConfirmationModal', {
        subscriptionId,
        projectId,
        plan: planName,
        nextRenewalAt,
        isWorkspaceSubscription,
        onConfirm: async (reason?: string) => {
          try {
            const res = await fetch(
              '/api/stripe/billing-portal?returnJson=true',
            )
            const data = await res.json()
            if (data.url) {
              const cancelUrl = `${data.url}/subscriptions/${subscriptionId}/cancel`
              window.open(cancelUrl, '_blank')
              onSuccess?.()
            }
          } catch (e) {
            console.error('Failed to open Stripe cancel URL', e)
            throw e
          }
        },
      })
    }, 100)
  }

  return (
    <DialogContent
      className="w-[95%] max-w-[600px] p-0 rounded-[20px] overflow-hidden !border-0 z-[80] !bg-transparent [&]:shadow-none"
      overlayClassName="z-[79]"
      noCloseButton
      style={{ border: 'none', background: 'transparent' }}
    >
      <div className="relative w-full h-full overflow-hidden rounded-[20px]">
        {/* 使用与主页相同的背景图片 */}
        <CancelModalBackgroundSimple />

        {/* 内容区域 */}
        <div className="relative p-8 sm:p-10 flex flex-col gap-6 z-10">
          {/* 关闭按钮 */}
          <button
            type="button"
            onClick={() => popModal()}
            className="absolute right-5 top-5 w-5 h-5 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* 标题 */}
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-white leading-[1.6] pr-8">
              {t('title', { plan: planName })}
            </DialogTitle>
          </DialogHeader>

          {/* 金色提示横幅 */}
          <div className="w-full py-2 px-4 rounded-lg bg-[rgba(253,213,119,0.2)] text-center">
            <p className="text-sm text-[#FDD577]">
              {t('banner', { days: daysLeft, plan: planName })}
            </p>
          </div>

          {/* 描述文字 */}
          <p className="text-base text-white leading-[1.6]">
            {t('description', { plan: planName })}
          </p>

          {/* 功能卡片网格 */}
          <div className="flex flex-col gap-3">
            {/* 第一行 */}
            <div className="flex gap-3">
              <FeatureCard
                icon={<Coins className="w-10 h-10 text-white" />}
                text={getFeatureCredits()}
              />
              <FeatureCard
                icon={<Globe className="w-10 h-10 text-white" />}
                text={t('feature_domain')}
              />
              <FeatureCard
                icon={<Blocks className="w-10 h-10 text-white" />}
                text={t('feature_integrations')}
              />
            </div>
            {/* 第二行 */}
            <div className="flex gap-3">
              <FeatureCard
                icon={<InfinityIcon className="w-10 h-10 text-white" />}
                text={t('feature_projects')}
              />
              <FeatureCard
                icon={<FileText className="w-10 h-10 text-white" />}
                text={t('feature_forms')}
              />
              <FeatureCard
                icon={<CircleDollarSign className="w-10 h-10 text-white" />}
                text={t('feature_commissions')}
              />
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex justify-end items-center gap-5 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="text-sm text-white/60 hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? <Loader2 className="animate-spin h-4 w-4" /> : null}
              {t('button_continue')}
            </button>
            <Button
              type="button"
              onClick={() => popModal()}
              disabled={isLoading}
              className="rounded-xl px-6 py-2 bg-white text-[#222222] font-semibold hover:bg-white/90"
            >
              {t('button_keep')}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
